import { Link, useLocation } from "react-router-dom";
import { useRef, useState, useEffect } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";
import { useLocalizedPath } from "@/hooks/useLocalizedPath";

interface Tag {
  name: string;
  slug: string;
  postCount?: number;
}

interface TagNavigationProps {
  tags: Tag[];
  activeTag?: string;
  showAll?: boolean;
}

const TagNavigation = ({ tags, activeTag, showAll = true }: TagNavigationProps) => {
  const location = useLocation();
  const { getLocalizedPath } = useLocalizedPath();
  const isAllActive = !activeTag && (location.pathname === "/blog" || location.pathname === "/en/blog");
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const checkScroll = () => {
    const container = scrollContainerRef.current;
    if (container) {
      setCanScrollLeft(container.scrollLeft > 0);
      setCanScrollRight(
        container.scrollLeft < container.scrollWidth - container.clientWidth - 1
      );
    }
  };

  useEffect(() => {
    checkScroll();
    window.addEventListener("resize", checkScroll);
    return () => window.removeEventListener("resize", checkScroll);
  }, [tags]);

  const scroll = (direction: "left" | "right") => {
    const container = scrollContainerRef.current;
    if (container) {
      const scrollAmount = 200;
      container.scrollBy({
        left: direction === "left" ? -scrollAmount : scrollAmount,
        behavior: "smooth",
      });
    }
  };

  return (
    <div className="relative flex items-center">
      {/* Left scroll button */}
      {canScrollLeft && (
        <button
          onClick={() => scroll("left")}
          className="absolute left-0 z-10 hidden md:flex h-8 w-8 items-center justify-center rounded-full bg-background/90 shadow-md border border-border hover:bg-muted transition-colors"
          aria-label="Scroll left"
        >
          <ChevronLeft className="h-4 w-4" />
        </button>
      )}

      <nav
        ref={scrollContainerRef}
        onScroll={checkScroll}
        className="flex items-center gap-2 overflow-x-auto scrollbar-hide py-2 -mx-4 px-4 sm:mx-0 sm:px-0 md:px-10"
      >
        {showAll && (
          <Link
            to={getLocalizedPath("/blog")}
            className={cn(
              "px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors",
              isAllActive
                ? "bg-primary text-primary-foreground"
                : "bg-secondary text-secondary-foreground hover:bg-secondary/80"
            )}
          >
            All
          </Link>
        )}

        {tags.map((tag) => (
          <Link
            key={tag.slug}
            to={getLocalizedPath(`/blog/tag/${tag.slug}`)}
            className={cn(
              "px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors",
              activeTag === tag.slug
                ? "bg-primary text-primary-foreground"
                : "bg-secondary text-secondary-foreground hover:bg-secondary/80"
            )}
          >
            {tag.name}
            {tag.postCount !== undefined && tag.postCount > 0 && (
              <span className="ml-1.5 text-xs opacity-70">({tag.postCount})</span>
            )}
          </Link>
        ))}
      </nav>

      {/* Right scroll button */}
      {canScrollRight && (
        <button
          onClick={() => scroll("right")}
          className="absolute right-0 z-10 hidden md:flex h-8 w-8 items-center justify-center rounded-full bg-background/90 shadow-md border border-border hover:bg-muted transition-colors"
          aria-label="Scroll right"
        >
          <ChevronRight className="h-4 w-4" />
        </button>
      )}
    </div>
  );
};

export default TagNavigation;
